import { loadGoals, loadGoalsCorrelations } from '@store/contentApi/contentApiState';
import { selectIsLlinkidInitializing } from '@store/llinkidApis/llinkidApiSelectors';
import { getActivityBatch, getAttachmentBatch } from '@store/llinkidApis/llinkidApiHelper';
import commonUtils from '@kathondvla/sri-client/common-utils';
import { loadGoalsFromParamsSaga, typedSelect, waitForSaga } from '@utils/sagaUtils';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import { isSameDay } from 'date-fns/isSameDay';

import { trackDuplicateActivity } from '@store/tracking/trackingSagas';
import { generalLoadError, generalSaveError } from '@store/alerts/alertConstants';
import { setAlert } from '@store/alerts/alertsState';
import { saveLlinkidApiBatch } from '@store/llinkidApis/llinkidApiState';
import { getKeyFromHref } from '@utils/getKeyFromHref';
import { logAndCaptureException } from '@utils/logAndCaptureException';
import type { BatchItem, BatchRequest } from '../../types/sriTypes';
import { getMoveWeekBatch, moveStartDateWeeks } from './calendarHelper';
import { postActivityBatchSaga, postAttachmentsSaga } from './calendarSagaHelpers';
import {
  selectActiveCustomCurriculaParamsFromPlans,
  selectActivitiesForPlans,
  selectBatchFromEditModal,
  selectExtraGoalsToLoadFromPlans,
  selectPlansVM,
} from './calendarSelectors';
import {
  deleteActivity,
  duplicateActivityModalClose,
  confirmDuplicateActivity,
  editActivityModalClose,
  loadGoalsForPlans,
  loadTranslationsToNewCurricula,
  moveWeek,
  saveActivity,
  saveActivityFailed,
  setCalendarKeys,
  setScrollToDate,
} from './calendarState';
import type {
  ConfirmDuplicateActivityPayload,
  DeleteActivityPayload,
  LoadGoalsForPlansPayload,
  LoadTranslationsPayload,
  MoveWeekPayload,
  SaveActivityPayload,
  SetCalendarKeysPayload,
} from './calendarTypes';
import type { RootState } from '@store/storeSetup';
import type { ApiActivity } from '../../types/llinkidApiTypes';

function* loadExtraData(action: LoadGoalsForPlansPayload) {
  try {
    const { plans } = action.payload;
    yield call(waitForSaga, (state: RootState) => selectIsLlinkidInitializing(state) === false);
    yield call(
      waitForSaga,
      (state: RootState) =>
        Object.keys(state.customCurriculaData.activityPlans).length &&
        Object.keys(state.customCurriculaData.activities).length &&
        Object.keys(state.customCurriculaData.customCurricula).length,
    );
    yield call(
      waitForSaga,
      (state: RootState) => selectActiveCustomCurriculaParamsFromPlans(state, { plans }) !== null,
    );
    const curriculaParams = yield* typedSelect(
      (state: RootState) => selectActiveCustomCurriculaParamsFromPlans(state, { plans }), // this is generating an error on refresh
    );
    const refresh: boolean = yield* typedSelect((state: RootState) => state.curriculum.preview);
    // for the copy modal, we don't need ALL the goals of the selected curricula, we only need the ones that have been included in the activities.
    // however, when opening the calendar, we WILL need all the goals. so we have used the same logic for simplicity.
    if (curriculaParams) {
      yield all(curriculaParams.map((params) => loadGoalsFromParamsSaga(params)));
    }

    const extraGoalsFromPlan = yield* typedSelect((state: RootState) =>
      selectExtraGoalsToLoadFromPlans(state, { plans }),
    );

    if (extraGoalsFromPlan.length) {
      yield put(loadGoals({ keys: extraGoalsFromPlan.map((e) => getKeyFromHref(e)), refresh }));
    }
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalLoadError));
  }
}

function* loadCurricula(action: SetCalendarKeysPayload) {
  try {
    const newAction: LoadGoalsForPlansPayload = {
      type: 'calendar/loadGoalsForPlans',
      payload: {
        plans: action.payload.calendarKeys.map((e) => {
          return { key: e };
        }),
      },
    };
    yield loadExtraData(newAction);
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalLoadError));
  }
}

function* loadTranslationData(action: LoadTranslationsPayload) {
  try {
    const { selectedCurriculum, originalCurriculum } = action.payload;

    if (!originalCurriculum) return;

    const shouldLoadCorrelation: boolean = yield* typedSelect(
      (state: RootState) =>
        !state.contentApiData.goalsCorrelationsLoading &&
        Object.keys(state.contentApiData.goalsCorrelations).length === 0,
    );

    if (shouldLoadCorrelation) {
      yield put(loadGoalsCorrelations());
    }

    yield all(selectedCurriculum.map((params) => loadGoalsFromParamsSaga(params)));
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalLoadError));
  }
}

function* deleteAllActivity(action: DeleteActivityPayload) {
  try {
    const { parentHrefs } = action.payload;

    const batch: BatchRequest = [];
    const parents = new Set<string>(parentHrefs);
    parentHrefs.forEach((href) => {
      batch.push({ verb: 'DELETE', href });
    });

    const activities: Array<ApiActivity> = yield* typedSelect((state: RootState) =>
      Object.values(state.customCurriculaData.activities),
    );
    const children = activities.filter((act) => parents.has(act.parent?.href));
    children.forEach((activity) => {
      batch.push({ verb: 'DELETE', href: activity.$$meta.permalink });
    }); // Do we want to delete att?
    yield put(saveLlinkidApiBatch({ batch, applyInstantly: true }));
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalSaveError));
  }
}

function* saveActivitySaga(action: SaveActivityPayload) {
  try {
    const modalData = action.payload;

    const result = yield* typedSelect((state: RootState) =>
      selectBatchFromEditModal(state, modalData),
    );
    const [batch, attachments] = result;

    yield postActivityBatchSaga({ payload: { batch, asyncSaving: false } });
    // yield delay(2000);
    // throw new Error('fail');
    yield postAttachmentsSaga({ payload: { attachments, asyncSaving: false } });
    yield put(editActivityModalClose());
    yield put(
      setAlert({
        key: 'edit-activity-success',
        msg: `Activiteit ${modalData.isCreate ? 'aangemaakt' : 'aangepast'}.`,
        type: 'success',
        showClose: true,
        delay: 2000,
      }),
    );
  } catch (e) {
    logAndCaptureException(e);
    yield put(
      setAlert({
        key: 'edit-activity-error',
        msg: 'Er is een probleem opgetreden bij het bewaren. Probeer even later opnieuw.',
        title: 'Fout',
        type: 'error',
        showClose: true,
        delay: 10000,
      }),
    );
    yield put(saveActivityFailed());
  }
}

function* moveWeekSaga(action: MoveWeekPayload) {
  try {
    const { startDate, type } = action.payload;

    const allActivities = yield* typedSelect(selectActivitiesForPlans);
    const batch = getMoveWeekBatch(allActivities, startDate, type);

    yield postActivityBatchSaga({ payload: { batch, asyncSaving: true } });
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalSaveError));
  }
}

function* confirmDuplicateActivitySaga(action: ConfirmDuplicateActivityPayload) {
  try {
    const { startDate, model, classes } = action.payload;
    const plansVM = yield* typedSelect(selectPlansVM);

    const shiftedWeeks = moveStartDateWeeks(startDate, model.weeks);
    const parentPeriod = {
      startDate: new Date(shiftedWeeks[0].period.startDate),
      endDate: new Date(shiftedWeeks[shiftedWeeks.length - 1].period.endDate),
    };

    const title = `${model.title} - kopie`;

    const existingTitles = new Set(
      plansVM
        .filter((p) => classes.some((c) => c.href === p.class.href))
        .flatMap((p) => p.activities.filter((a) => !a.parent.href.includes('/activities/')))
        .filter((a) => isSameDay(a.period.dtStartDate, new Date(startDate)))
        .map((a) => a.title),
    );

    let uniqueTitle = title;
    let counter = 2;
    while (existingTitles.has(uniqueTitle)) {
      uniqueTitle = `${title} (${counter})`;
      counter++;
    }

    const batch: BatchRequest = [];
    const attachmentsToUpload: { items: Array<any>; files: Array<File> } = {
      items: [],
      files: [],
    };

    classes.forEach((cls) => {
      const plan = plansVM.find((e) => e.class.href === cls.href);
      if (!plan) return;

      const parentKey = commonUtils.generateUUID();
      batch.push(
        getActivityBatch({
          key: parentKey,
          parentHref: plan.$$meta.permalink,
          activity: {
            title: uniqueTitle,
            goals: model.goals.map((g) => ({ href: g.href })),
          },
          period: parentPeriod,
        }) as BatchItem<'PUT'>,
      );

      shiftedWeeks.forEach((week) => {
        const childKey = commonUtils.generateUUID();
        batch.push(
          getActivityBatch({
            key: childKey,
            parentHref: `/llinkid/activityplanning/activityplans/activities/${parentKey}`,
            activity: {
              description: week.description,
              goals: week.goals.map((g) => ({ href: g.href })),
            },
            period: {
              startDate: new Date(week.period.startDate),
              endDate: new Date(week.period.endDate),
            },
          }) as BatchItem<'PUT'>,
        );

        if (week.attachments) {
          week.attachments.forEach((att) => {
            if (att.$$expanded?.href) {
              attachmentsToUpload.items.push(
                getAttachmentBatch({ activityKey: childKey, attachment: att.$$expanded }),
              );
            }
          });
        }
      });
    });

    yield postActivityBatchSaga({ payload: { batch, asyncSaving: false } });
    yield postAttachmentsSaga({
      payload: { attachments: attachmentsToUpload, asyncSaving: false },
    });
    yield put(duplicateActivityModalClose());
    yield put(setScrollToDate(startDate));
    yield put(trackDuplicateActivity());
    yield put(
      setAlert({
        key: 'duplicate-activity-success',
        msg: 'Activiteit gedupliceerd.',
        type: 'success',
        showClose: true,
        delay: 2000,
      }),
    );
  } catch (e) {
    logAndCaptureException(e);
    yield put(
      setAlert({
        key: 'duplicate-activity-error',
        msg: 'Er is een probleem opgetreden bij het dupliceren. Probeer even later opnieuw.',
        title: 'Fout',
        type: 'error',
        showClose: true,
        delay: 10000,
      }),
    );
    yield put(duplicateActivityModalClose());
  }
}

export function* watcherSaga() {
  yield takeEvery(loadGoalsForPlans, loadExtraData);
  yield takeEvery(setCalendarKeys, loadCurricula);
  yield takeEvery(loadTranslationsToNewCurricula, loadTranslationData);
  yield takeEvery(deleteActivity, deleteAllActivity);
  yield takeEvery(saveActivity, saveActivitySaga);
  yield takeEvery(moveWeek, moveWeekSaga);
  yield takeEvery(confirmDuplicateActivity, confirmDuplicateActivitySaga);
}
